import { create } from 'zustand';

import { AiDataPolicyConflictError } from './errors';
import { getAiDataPolicyService } from './service';
import {
  policyStamp,
  samePolicy,
  type AiDataPolicy,
  type AiDataPolicyService,
  type AiDataPolicyWorkspace,
  type SetAiDataPolicyInput,
  type SetAiDataPolicyResult,
} from './types';

export interface PolicyTransition {
  previous: AiDataPolicy;
  current: AiDataPolicy;
  at: number;
}

export type AiDataPolicyStatus = 'idle' | 'loading' | 'ready' | 'error';

export type AiDataPolicyRole = 'admin' | 'member';

interface AiDataPolicyState {
  policy: AiDataPolicy | null;
  status: AiDataPolicyStatus;
  remoteChange: PolicyTransition | null;
  workspace: AiDataPolicyWorkspace | null;
  role: AiDataPolicyRole | null;
  load: () => Promise<AiDataPolicy | null>;
  ensure: () => Promise<AiDataPolicy | null>;
  save: (
    input: SetAiDataPolicyInput,
    expected?: AiDataPolicy | null,
  ) => Promise<SetAiDataPolicyResult>;
  loadWorkspace: () => Promise<AiDataPolicyWorkspace | null>;
  applyRemote: (policy: AiDataPolicy) => void;
  clearRemoteChange: () => void;
}

let subscribedTo: AiDataPolicyService | null = null;
let unsubscribe: (() => void) | null = null;
let inflight: Promise<AiDataPolicy | null> | null = null;
let saves = 0;
let workspaceInflight: Promise<AiDataPolicyWorkspace | null> | null = null;

function watch(service: AiDataPolicyService, applyRemote: (policy: AiDataPolicy) => void): void {
  if (subscribedTo === service) return;
  unsubscribe?.();
  subscribedTo = service;
  unsubscribe = service.subscribe(applyRemote);
}

function isNewer(next: AiDataPolicy, previous: AiDataPolicy): boolean {
  return policyStamp(next) > policyStamp(previous);
}

export const useAiDataPolicyStore = create<AiDataPolicyState>()((set, get) => {
  const adopt = (policy: AiDataPolicy) => {
    const previous = get().policy;
    set({
      policy,
      status: 'ready',
      remoteChange:
        previous && !samePolicy(previous, policy)
          ? { previous, current: policy, at: Date.now() }
          : get().remoteChange,
    });
  };

  const accept = (policy: AiDataPolicy, fromServer = false) => {
    const previous = get().policy;
    if (!previous) {
      set({ policy, status: 'ready' });
      return;
    }
    if (isNewer(policy, previous) || (fromServer && !samePolicy(policy, previous))) {
      adopt(policy);
      return;
    }
    if (samePolicy(policy, previous) && !previous.setBy?.name && policy.setBy?.name) {
      set({ policy, status: 'ready' });
      return;
    }
    set({ status: 'ready' });
  };

  return {
    policy: null,
    status: 'idle',
    remoteChange: null,
    workspace: null,
    role: null,

    load() {
      if (inflight) return inflight;
      const service = getAiDataPolicyService();
      watch(service, (policy) => {
        get().applyRemote(policy);
      });
      if (!get().policy) set({ status: 'loading' });
      const issuedAt = saves;
      inflight = service
        .getPolicy()
        .then((policy) => {
          accept(policy, issuedAt === saves);
          return get().policy;
        })
        .catch(() => {
          if (!get().policy) set({ status: 'error' });
          return null;
        })
        .finally(() => {
          inflight = null;
        });
      return inflight;
    },

    ensure() {
      const { policy, status } = get();
      if (policy && status === 'ready') return Promise.resolve(policy);
      return get().load();
    },

    async save(input, expected) {
      const service = getAiDataPolicyService();
      if (expected !== undefined) {
        const current = await service.getPolicy();
        if (!samePolicy(current, expected)) {
          accept(current, true);
          throw new AiDataPolicyConflictError(current);
        }
      }
      const result = await service.setPolicy(input);
      saves += 1;
      if (result.changed) {
        set({ policy: result.policy, status: 'ready' });
      } else {
        accept(result.policy, true);
      }
      return result;
    },

    loadWorkspace() {
      const known = get().workspace;
      if (known) return Promise.resolve(known);
      workspaceInflight ??= getAiDataPolicyService()
        .getWorkspace()
        .then((workspace) => {
          set({ workspace });
          return workspace;
        })
        .catch(() => null)
        .finally(() => {
          workspaceInflight = null;
        });
      return workspaceInflight;
    },

    applyRemote(policy) {
      const previous = get().policy;
      if (previous && !isNewer(policy, previous)) return;
      adopt(policy);
    },

    clearRemoteChange() {
      set({ remoteChange: null });
    },
  };
});

export function resetAiDataPolicyStore(): void {
  unsubscribe?.();
  unsubscribe = null;
  subscribedTo = null;
  inflight = null;
  workspaceInflight = null;
  useAiDataPolicyStore.setState({
    policy: null,
    status: 'idle',
    remoteChange: null,
    workspace: null,
    role: null,
  });
}
